import type { TeammateId } from "@repo/app-types/entities/teammate";
import type {
  CustomerInteractionCancelableCallTask,
  GradingId,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { AlertDialogVariant } from "@repo/ui/Modal/alertDialog";
import { ConfirmationModal } from "@repo/ui/Modal/confirmationModal";
import { useState, type FC } from "react";
import { useCancelTeammateAgentCallTaskMutation } from "../../api/entities/teammateAgent.js";
import { Can } from "../../helpers/teammateAuthorizationContext.js";
import { useAppDispatch } from "../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../notificationsOverlay/notificationsSlice.js";

export interface CancelCallTaskButtonProps {
  callTask: CustomerInteractionCancelableCallTask;
  /** The interaction the task belongs to, so its cache entry refreshes. */
  gradingId: GradingId;
  /** The agent the task belongs to; the cancel is scoped to it. */
  teammateAgentId: TeammateId;
  /** Who the task is reaching out to, for the confirmation copy. */
  customerName: string | undefined;
}

/**
 * Stops an interaction's running call task, whatever it is doing: waiting for
 * its next call or turn, dialing, or on a call.
 */
export const CancelCallTaskButton: FC<CancelCallTaskButtonProps> = (props) => {
  const { callTask, gradingId, teammateAgentId, customerName } = props;

  const dispatch = useAppDispatch();
  const [isConfirming, setIsConfirming] = useState(false);
  const [cancelCallTask, { isLoading: isCanceling }] =
    useCancelTeammateAgentCallTaskMutation();

  const isAgentManaged = callTask.is_agent_managed;
  const who = customerName || "this customer";
  const actionLabel = isAgentManaged ? "Stop outreach" : "Cancel calls";

  const handleCancel = async () => {
    // Ids are unique per notification: `showNotification` prepends without
    // deduping, so a repeat with the same id both duplicates a React key and
    // lets the older toast's dismissal timer take the newer one with it.
    const notificationId = `cancel-call-task-${callTask.id}-${Date.now()}`;

    try {
      const result = await cancelCallTask({
        teammateAgentId,
        callTaskId: callTask.id,
        gradingId,
      }).unwrap();

      showNotification(dispatch, {
        id: notificationId,
        primaryMessage: resolveCancelMessage(result, isAgentManaged),
        type: NotificationType.SUCCESS,
      });
    } catch {
      showNotification(dispatch, {
        id: notificationId,
        primaryMessage: isAgentManaged
          ? "Failed to stop the outreach"
          : "Failed to cancel the calls",
        type: NotificationType.FAILURE,
      });
    }
  };

  // The endpoint requires updating teammates; don't offer what would be refused.
  return (
    <Can I={PermissionActions.UPDATE} a={PermissionSubjects.TEAMMATES}>
      <Button
        size={Size.SMALL}
        variant={ButtonVariant.Warning}
        isDisabled={isCanceling}
        onClick={() => setIsConfirming(true)}
      >
        {actionLabel}
      </Button>

      <ConfirmationModal
        isOpen={isConfirming}
        onOpenChange={setIsConfirming}
        title={isAgentManaged ? "Stop this outreach?" : "Cancel these calls?"}
        confirmText={actionLabel}
        cancelText={isAgentManaged ? "Keep going" : "Keep calls"}
        variant={AlertDialogVariant.Warning}
        onConfirm={() => void handleCancel()}
      >
        {isAgentManaged
          ? `The agent won't text or call ${who} about this again. A call already underway can finish, and everything so far stays on the timeline.`
          : `No more calls will be placed to ${who} for this interaction. A call already underway can finish.`}
      </ConfirmationModal>
    </Can>
  );
};

/**
 * The endpoint reports three outcomes, not two. A call already underway is not
 * torn down, and a task that had already finished was never cancelable —
 * claiming either as "canceled" would describe something that didn't happen.
 */
function resolveCancelMessage(
  result: { canceled: boolean; cancelRequested: boolean },
  isAgentManaged: boolean,
): string {
  if (result.canceled) {
    return isAgentManaged ? "Outreach stopped" : "Calls canceled";
  }

  if (!result.cancelRequested) {
    return isAgentManaged
      ? "This outreach had already finished, so there was nothing to stop"
      : "These calls had already finished, so there was nothing to cancel";
  }

  return isAgentManaged
    ? "Outreach stops after the call in progress"
    : "Calls canceled for every attempt after the one in progress";
}
